import { Location, NavigateFunction } from 'react-router-dom'
import queryString from 'query-string'
import { RteModalQueryParams } from 'src/components/RichTextEditor'
import {
  PolicyDocRecord,
  PolicyRecord,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import {
  ACTOR_TYPE,
  Document as DocumentMsg,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { DocumentObj } from 'src/adapter/AttachmentAdapter'
import { PolicyListSnapshot } from './Policies.types'

/**
 * Maps a PolicyDocRecord (Policy.ListPolicyDocuments / GetPolicyDocument) to the
 * legacy attachment Document message shape existing viewers consume.
 *
 * linkerModelId stays empty: policy_documents has no linker column, and an
 * externally sourced doc is described by isLinkedDocument + docImportLink +
 * actor (the receptor model id), which every consumer reads instead.
 */
export const policyDocRecordToDocumentMsg = (
  record: PolicyDocRecord,
): DocumentMsg => {
  const msg = new DocumentMsg()
    .setId(record.getId())
    .setCreatedat(record.getCreatedAt())
    .setSubjectmodeltype(MODEL_TYPE.POLICY)
    .setSubjectmodelid(record.getPolicyModelId())
    .setActortype(
      record.getIsAutomated() ? ACTOR_TYPE.RECEPTOR : ACTOR_TYPE.USER,
    )
    .setActor(record.getActor())
    .setMime(record.getMime())
    .setCaption(record.getCaption())
    .setDescription(record.getDescription())
    .setContentid(record.getContentId())
    .setSourceServiceModelId(record.getSourceServiceModelId())
    .setIsLinkedDocument(record.getIsLinkedDocument())
    .setIsLinkBroken(record.getIsLinkBroken())
    .setReceptorId(record.getReceptorId())
    .setReceptorModelId(record.getReceptorModelId())
    .setDocImportKey(record.getDocImportKey())
    .setDocImportLink(record.getDocImportLink())
    .setIsInstanceMissing(record.getIsInstanceMissing())
    .setNewInstanceId(record.getInstanceId())
    .setIndexingStatus(record.getIndexingStatus())
  const relevantDate = record.getRelevantDate()
  if (relevantDate) {
    msg.setRelevantdate(relevantDate)
  }
  const docModifiedAt = record.getDocModifiedAt()
  if (docModifiedAt) {
    msg.setDocModifiedAt(docModifiedAt)
  }
  const docScannedAt = record.getDocScannedAt()
  if (docScannedAt) {
    msg.setDocScannedAt(docScannedAt)
  }
  const lastReviewedDate = record.getLastReviewedDate()
  if (lastReviewedDate) {
    msg.setLastReviewedDate(lastReviewedDate)
  }
  const lastApprovedDate = record.getLastApprovedDate()
  if (lastApprovedDate) {
    msg.setLastApprovedDate(lastApprovedDate)
  }
  return msg
}

/**
 * Maps a PolicyDocRecord to the legacy DocumentObj shape the policy document
 * grid components consume.
 */
export const policyDocRecordToDocumentObj = (
  record: PolicyDocRecord,
): DocumentObj => policyDocRecordToDocumentMsg(record).toObject() as DocumentObj

export const editDocumentMarkdown = (
  documentId: string,
  material: string,
  setMarkdownBody: React.Dispatch<React.SetStateAction<string>>,
  navigate: NavigateFunction,
  location: Location,
): void => {
  setMarkdownBody(material)
  navigate(
    queryString.stringifyUrl(
      {
        url: location.pathname,
        query: {
          ...queryString.parse(location.search, {
            arrayFormat: 'bracket',
          }),
          [RteModalQueryParams.SHOW_RTE_MODAL]: true,
          [RteModalQueryParams.UNIQUE_ID]: documentId,
          [RteModalQueryParams.SHOW_WIZARD]: false,
        },
      },
      {
        arrayFormat: 'bracket',
      },
    ),
  )
}

export type PolicyFieldSnapshot = {
  description: string
  lastReviewedDate: number
  lastApprovedDate: number
}

export const getPolicyFieldSnapshot = (
  policy: PolicyRecord | undefined,
): PolicyFieldSnapshot => ({
  description: policy?.getDescription() ?? '',
  lastReviewedDate: policy?.getLastReviewedDate()?.getSeconds() ?? 0,
  lastApprovedDate: policy?.getLastApprovedDate()?.getSeconds() ?? 0,
})

export const policyFieldsChanged = (
  initial: PolicyFieldSnapshot,
  current: PolicyFieldSnapshot,
): boolean =>
  current.description !== initial.description ||
  current.lastReviewedDate !== initial.lastReviewedDate ||
  current.lastApprovedDate !== initial.lastApprovedDate

export function isAnyPolicyIndexing(policies: PolicyRecord[]): boolean {
  return policies.some(
    (p) =>
      p.getIsNameDeptIndexing() ||
      p.getIsDescriptionIndexing() ||
      p.getArePolicyDocDatesIndexing(),
  )
}

export function snapshotPolicies(policies: PolicyRecord[]): PolicyListSnapshot {
  return new Map(
    policies.map((p) => [
      p.getModelId(),
      { name: p.getName(), departmentId: p.getDepartmentId() },
    ]),
  )
}

export function getArePolicySnapshotsEqual(
  prev: PolicyListSnapshot,
  next: PolicyListSnapshot,
): boolean {
  if (prev.size !== next.size) return false

  for (const [modelId, prevPolicy] of prev.entries()) {
    const nextPolicy = next.get(modelId)
    if (!nextPolicy) return false
    if (
      prevPolicy.name !== nextPolicy.name ||
      prevPolicy.departmentId !== nextPolicy.departmentId
    ) {
      return false
    }
  }

  return true
}
